import { useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BlockIcon from '@mui/icons-material/Block';
import { useDataProvider, useNotify } from 'react-admin';

import type { IrmDataProvider } from '../../providers/types';

import { CreateApiKeyDialog } from './CreateApiKeyDialog';
import { ACTIVE_KEY_EXISTS, canHoldKey } from './apiKeys';
import { formatDate, fullDate } from './keyDates';
import { KEEP_DIALOG_SIZE } from '../../root/touchTheme';
import {
    createApiKey,
    revokeApiKey,
    useUserApiKeys,
    type KeyRecord,
} from './useUserApiKeys';

export interface KeyHolder {
    id: string;
    email?: string;
    role?: string;
    status?: string;
    allowed_features?: string[];
}

/**
 * Partner API keys for one user, rendered under the user's edit form.
 *
 * Keys only ever belong to a user, so they are managed where the user is —
 * next to the `api` feature checkbox that decides whether the user may hold
 * one at all. There is deliberately no cross-user key list: a key is reached
 * through its owner.
 */
export const UserApiKeys = ({
    user,
    managedInConsole = false,
}: {
    user: KeyHolder;
    /** The owner's record is read-only here (an admin's own page), so its
     *  status and features are changed in the Firestore console. */
    managedInConsole?: boolean;
}) => {
    const [creating, setCreating] = useState(false);
    const [revoking, setRevoking] = useState(false);
    // Revoking cannot be undone -- nothing sets a key back to 'active', and
    // the raw key is gone, so it cannot even be re-minted. One click is too
    // little between a misfire and a dead partner integration.
    const [confirming, setConfirming] = useState<KeyRecord | null>(null);
    const notify = useNotify();
    const dataProvider = useDataProvider<IrmDataProvider>();

    // Live: a create or revoke lands in the table without a refetch.
    const { keys, loading, error, errorCode } = useUserApiKeys(user.id);
    const eligible = canHoldKey(user);
    // One active key per user. adminCreateApiKey refuses a second one with
    // active_key_exists whatever this button says; disabling it only spares
    // the admin a dialog that is bound to fail. A list that is loading or
    // failed does not count (the hook keeps the previous keys then); the
    // server still decides.
    const hasActive =
        !loading && !error && keys.some(k => k.status === 'active');

    const revoke = async (record: KeyRecord) => {
        setConfirming(null);
        setRevoking(true);
        try {
            await revokeApiKey(dataProvider, record.id);
            notify(`Revoked ${record.prefix}. Live within 60 seconds.`, {
                type: 'info',
            });
        } catch (e) {
            notify(`Could not revoke: ${String(e)}`, { type: 'error' });
        } finally {
            setRevoking(false);
        }
    };

    return (
        <Card sx={{ mt: 3 }}>
            <CardContent>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 1 }}
                >
                    <Typography variant="h6">API keys</Typography>
                    <Tooltip title={hasActive ? ACTIVE_KEY_EXISTS : ''}>
                        {/* A disabled button fires no events, so the tooltip
                            hangs on a wrapper. */}
                        <span>
                            <Button
                                size="small"
                                startIcon={<AddIcon />}
                                disabled={!eligible || hasActive}
                                onClick={() => setCreating(true)}
                            >
                                Create key
                            </Button>
                        </span>
                    </Tooltip>
                </Stack>

                {/* Existing keys stay listed even when the user is no longer
                    eligible — that is exactly when an admin needs to see and
                    revoke them. Only minting a new one is blocked. */}
                {!eligible && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        {managedInConsole ? (
                            <>
                                This account cannot hold a key: it needs status{' '}
                                <strong>approved</strong>. Admin records are
                                edited in the Firestore console, so set it
                                there.
                            </>
                        ) : (
                            <>
                                This user cannot hold a key: it needs status{' '}
                                <strong>approved</strong> and the{' '}
                                <strong>API</strong> feature, which is never a
                                role default. Grant both above, then save.
                            </>
                        )}
                    </Alert>
                )}

                {/* Say which failure it is. Both likely causes are deploy
                    state rather than bugs here, and they have different fixes,
                    so a bare "could not load" sends an admin looking in the
                    wrong place — including at users who simply hold no keys. */}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {errorCode === 'permission-denied' ? (
                            <>
                                Could not load this user's keys — permission
                                denied. The Firestore rules for{' '}
                                <code>api_keys</code> have not been deployed
                                yet, so every user reads as an error here,
                                whether or not they hold a key.
                            </>
                        ) : errorCode === 'failed-precondition' ? (
                            <>
                                Could not load this user's keys — the Firestore
                                index this list needs is missing. It is the
                                composite index on <code>uid</code> and{' '}
                                <code>created_at</code>.
                            </>
                        ) : (
                            <>
                                Could not load this user's keys
                                {errorCode ? ` (${errorCode})` : ''}.
                            </>
                        )}
                    </Alert>
                )}

                {/* An error already showed its alert above, and a bare six-column
                    header under it says nothing; a loading table says it even
                    less convincingly. Only render the table once there is
                    something to put in it. */}
                {error ? null : loading ? (
                    <Stack alignItems="center" sx={{ py: 2 }}>
                        <CircularProgress size={20} />
                    </Stack>
                ) : keys.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        No keys issued.
                    </Typography>
                ) : (
                    <Box sx={{ overflowX: 'auto' }}>
                        <Table size="small" sx={{ minWidth: 480 }}>
                            <TableHead>
                                <TableRow>
                                    {/* The prefix, never the key — it is the only
                                    part of a credential allowed to appear in a
                                    UI or a log line. */}
                                    <TableCell>Key</TableCell>
                                    <TableCell>Label</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Created</TableCell>
                                    <TableCell>Last used</TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {keys.map(key => (
                                    <TableRow key={key.id}>
                                        <TableCell
                                            sx={{ fontFamily: 'monospace' }}
                                        >
                                            {key.prefix}
                                        </TableCell>
                                        <TableCell>{key.label}</TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={key.status}
                                                color={
                                                    key.status === 'active'
                                                        ? 'success'
                                                        : 'default'
                                                }
                                                variant={
                                                    key.status === 'active'
                                                        ? 'filled'
                                                        : 'outlined'
                                                }
                                            />
                                        </TableCell>
                                        <TableCell
                                            sx={{ whiteSpace: 'nowrap' }}
                                        >
                                            <Tooltip
                                                title={fullDate(key.created_at)}
                                            >
                                                <span>
                                                    {formatDate(key.created_at)}
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell
                                            sx={{ whiteSpace: 'nowrap' }}
                                        >
                                            <Tooltip
                                                title={fullDate(
                                                    key.last_used_at
                                                )}
                                            >
                                                <span>
                                                    {formatDate(
                                                        key.last_used_at
                                                    )}
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell align="right">
                                            {key.status === 'active' && (
                                                <Button
                                                    size="small"
                                                    color="error"
                                                    startIcon={<BlockIcon />}
                                                    disabled={revoking}
                                                    onClick={() =>
                                                        setConfirming(key)
                                                    }
                                                >
                                                    Revoke
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                )}
            </CardContent>

            <Dialog
                open={!!confirming}
                onClose={() => setConfirming(null)}
                className={KEEP_DIALOG_SIZE}
            >
                <DialogTitle>Revoke this key?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        <strong>{confirming?.prefix}</strong>
                        {confirming?.label ? ` — ${confirming.label}` : ''} will
                        stop working within 60 seconds. This cannot be undone:
                        the key itself is not recoverable, so restoring access
                        means issuing a new one and giving it to whoever uses
                        it.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirming(null)}>Cancel</Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={() => confirming && revoke(confirming)}
                    >
                        Revoke
                    </Button>
                </DialogActions>
            </Dialog>

            <Box>
                <CreateApiKeyDialog
                    open={creating}
                    recipient={user.email ?? user.id}
                    onClose={() => setCreating(false)}
                    onCreate={label =>
                        createApiKey(dataProvider, { uid: user.id, label })
                    }
                />
            </Box>
        </Card>
    );
};
